import React, { useEffect, useMemo, useState } from 'react';
import { ordersAPI } from '../services/api';
import { Search, RefreshCw, Pencil, Eye, CheckCircle, Truck, XCircle } from 'lucide-react';
import Modal from '../components/ui/Modal';

const statusClasses = {
  placed: 'bg-gray-100 text-gray-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const paymentStatusClasses = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-100 text-gray-800',
};

const formatPaymentMethod = (pm) => {
  if (!pm) return 'N/A';
  if (typeof pm === 'string') return pm.toUpperCase();
  const type = pm.type || '';
  if (type === 'cod') return 'Cash on Delivery';
  if (type === 'razorpay') return 'Online (Razorpay)';
  if (type === 'card') return `Card${pm.cardLast4 ? ` ****${pm.cardLast4}` : ''}`;
  return type ? type.toUpperCase() : 'N/A';
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shippingOrderId, setShippingOrderId] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await ordersAPI.getAdminOrders({ status: statusFilter, page, limit });
      setOrders(res.orders || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, limit]);

  const filteredOrders = useMemo(() => {
    if (!search) return orders;
    const s = search.toLowerCase();
    return orders.filter((o) =>
      (o.order_id || '').toLowerCase().includes(s) ||
      (o.customer_email || '').toLowerCase().includes(s) ||
      (o.customer_name || '').toLowerCase().includes(s)
    );
  }, [orders, search]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const openEdit = async (order) => {
    try {
      const detail = await ordersAPI.getAdminOrderDetail(order.order_id);
      setSelectedOrder(detail.order);
      setIsEditOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to fetch order');
    }
  };

  const openView = async (order) => {
    try {
      const detail = await ordersAPI.getAdminOrderDetail(order.order_id);
      setSelectedOrder(detail.order);
      setIsViewOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to fetch order');
    }
  };

  const handleSave = async (updates) => {
    if (!selectedOrder) return;
    setSaving(true);
    try {
      await ordersAPI.updateOrder(selectedOrder.order_id, updates);
      setIsEditOpen(false);
      setSelectedOrder(null);
      await fetchOrders();
    } catch (e) {
      setError(e.message || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateShipment = async (order) => {
    if (!window.confirm(`Create Shiprocket shipment for order ${order.order_id}?`)) {
      return;
    }

    setShippingOrderId(order.order_id);
    setError('');
    try {
      const res = await ordersAPI.createShipment(order.order_id);
      if (res.success) {
        await fetchOrders();
        if (selectedOrder?.order_id === order.order_id) {
          const detail = await ordersAPI.getAdminOrderDetail(order.order_id);
          setSelectedOrder(detail.order);
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to create shipment');
    } finally {
      setShippingOrderId(null);
    }
  };

  const canCancelOrder = (status) =>
    ['placed', 'processing', 'shipped'].includes(status);

  const handleCancelOrder = async (order) => {
    const isShipped = order.status === 'shipped' || Boolean(order.shipment?.awb_code);
    const confirmMessage = isShipped
      ? `Cancel order ${order.order_id}? This will also cancel the Shiprocket shipment/pickup so the courier does not come.`
      : `Cancel order ${order.order_id}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancellingOrderId(order.order_id);
    setError('');
    try {
      const res = await ordersAPI.cancelOrder(order.order_id);
      if (res.success) {
        await fetchOrders();
        if (selectedOrder?.order_id === order.order_id) {
          const detail = await ordersAPI.getAdminOrderDetail(order.order_id);
          setSelectedOrder(detail.order);
        }
      } else {
        setError(res.error || 'Failed to cancel order');
      }
    } catch (e) {
      setError(e.message || 'Failed to cancel order');
    } finally {
      setCancellingOrderId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Orders</h2>
          <p className="text-sm text-gray-600">Manage customer orders</p>
        </div>
        <button
          className="inline-flex items-center px-4 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          onClick={fetchOrders}
          disabled={loading}
        >
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters and search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search by Order ID, name, or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
          >
            <option value="">All Statuses</option>
            <option value="placed">Placed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td className="px-6 py-10 text-center text-gray-500" colSpan={7}>Loading...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td className="px-6 py-10 text-center text-red-600" colSpan={7}>{error}</td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td className="px-6 py-10 text-center text-gray-500" colSpan={7}>No orders found</td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{order.order_id}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{order.customer_name}</div>
                      <div className="text-xs text-gray-500">{order.customer_email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{order.items_count}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">₹{order.total_amount}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusClasses[order.status] || 'bg-gray-100 text-gray-800'}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${paymentStatusClasses[order.payment_status] || 'bg-gray-100 text-gray-800'}`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {!order.shipment?.awb_code && order.status !== 'cancelled' && (
                        <button
                          className="inline-flex items-center px-3 py-1.5 rounded-md border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-50"
                          onClick={() => handleCreateShipment(order)}
                          disabled={shippingOrderId === order.order_id}
                          title="Create Shipment"
                        >
                          <Truck size={16} className={shippingOrderId === order.order_id ? 'animate-pulse' : ''} />
                        </button>
                      )}
                      {canCancelOrder(order.status) && (
                        <button
                          className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                          onClick={() => handleCancelOrder(order)}
                          disabled={cancellingOrderId === order.order_id}
                          title="Cancel Order"
                        >
                          <XCircle size={16} className={cancellingOrderId === order.order_id ? 'animate-pulse' : ''} />
                        </button>
                      )}
                      <button
                        className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                        onClick={() => openView(order)}
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="inline-flex items-center px-3 py-1.5 rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => openEdit(order)}
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-white">
          <div className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </div>
          <div className="space-x-2">
            <button
              className="px-3 py-1.5 border rounded-md disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              Previous
            </button>
            <button
              className="px-3 py-1.5 border rounded-md disabled:opacity-50"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={`Order ${selectedOrder?.order_id}`} maxWidth="max-w-3xl">
        {selectedOrder && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Customer</h4>
                <div className="text-sm text-gray-700">{selectedOrder.customer_name}</div>
                <div className="text-xs text-gray-500">{selectedOrder.customer_email}</div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Order</h4>
                <div className="text-sm text-gray-700">Status: {selectedOrder.status}</div>
                <div className="text-sm text-gray-700">Payment: {selectedOrder.payment_status} ({formatPaymentMethod(selectedOrder.payment_method)})</div>
                {selectedOrder.payment_method?.razorpay_payment_id && (
                  <div className="text-xs text-gray-500">Payment ID: {selectedOrder.payment_method.razorpay_payment_id}</div>
                )}
                {selectedOrder.shipment?.awb_code && (
                  <>
                    <div className="text-sm text-gray-700 mt-1">AWB: {selectedOrder.shipment.awb_code}</div>
                    <div className="text-sm text-gray-700">Courier: {selectedOrder.shipment.courier_name || 'N/A'}</div>
                    {selectedOrder.shipment.tracking_url && (
                      <a
                        href={selectedOrder.shipment.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        Track on Shiprocket
                      </a>
                    )}
                  </>
                )}
                <div className="text-sm text-gray-700">Total: ₹{selectedOrder.total_amount}</div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Items</h4>
              <div className="border rounded-lg divide-y">
                {selectedOrder.items?.map((item, idx) => (
                  <div className="p-3 flex items-center justify-between" key={idx}>
                    <div className="flex items-center space-x-3">
                      {item.product_image && (
                        <img src={item.product_image} alt={item.product_name} className="w-12 h-12 rounded object-cover border" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.product_name}</div>
                        <div className="text-xs text-gray-500">Size: {item.size} • Qty: {item.quantity}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-900">₹{item.total}</div>
                  </div>
                ))}
              </div>
            </div>

            {canCancelOrder(selectedOrder.status) && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => handleCancelOrder(selectedOrder)}
                  disabled={cancellingOrderId === selectedOrder.order_id}
                  className="inline-flex items-center px-4 py-2 rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                >
                  <XCircle size={16} className="mr-2" />
                  {cancellingOrderId === selectedOrder.order_id ? 'Cancelling...' : 'Cancel Order'}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <EditOrderModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        order={selectedOrder}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
};

const EditOrderModal = ({ isOpen, onClose, order, onSave, saving }) => {
  const [status, setStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [shippingAddress, setShippingAddress] = useState({});

  useEffect(() => {
    if (order) {
      setStatus(order.status || '');
      setPaymentStatus(order.payment_status || '');
      setNotes(order.notes || '');
      setShippingAddress(order.shipping_address || {});
    }
  }, [order]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updates = {
      order_status: status,
      payment_status: paymentStatus,
      notes,
      shipping_address: shippingAddress,
    };
    onSave(updates);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={order ? `Edit Order ${order.order_id}` : 'Edit Order'} maxWidth="max-w-2xl">
      {!order ? (
        <div className="text-center text-gray-500">Loading...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="placed">Placed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <input
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                value={formatPaymentMethod(order.payment_method)}
                readOnly
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Shipping Address</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                className="px-3 py-2 border border-gray-300 rounded-lg"
                value={shippingAddress.name || ''}
                onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                placeholder="Full name"
              />
              <input
                className="px-3 py-2 border border-gray-300 rounded-lg"
                value={shippingAddress.phone || ''}
                onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                placeholder="Phone"
              />
              <input
                className="px-3 py-2 border border-gray-300 rounded-lg md:col-span-2"
                value={shippingAddress.address || ''}
                onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                placeholder="Address"
              />
              <input
                className="px-3 py-2 border border-gray-300 rounded-lg"
                value={shippingAddress.city || ''}
                onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                placeholder="City"
              />
              <input
                className="px-3 py-2 border border-gray-300 rounded-lg"
                value={shippingAddress.state || ''}
                onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                placeholder="State"
              />
              <input
                className="px-3 py-2 border border-gray-300 rounded-lg"
                value={shippingAddress.pincode || ''}
                onChange={(e) => setShippingAddress({ ...shippingAddress, pincode: e.target.value })}
                placeholder="Pincode"
              />
              <input
                className="px-3 py-2 border border-gray-300 rounded-lg"
                value={shippingAddress.country || ''}
                onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                placeholder="Country"
              />
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
              {saving ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default Orders;


